<script setup lang="ts">
import UICloseButton from '@/components/ui/UICloseButton.vue'
import UICollapseButton from '@/components/ui/UICollapseButton.vue'
import UIHeading from '@/components/ui/UIHeading.vue'

withDefaults(
  defineProps<{
    /** What text should be displayed in the header? */
    text: string
    /** Where is the header located (affects spacing)? */
    location?: keyof typeof locationMapping
    /** What variant should be used for the header coloration? */
    variant?: keyof typeof variantMapping
    /** What should the heading font size be? */
    headingSize?: 'serif' | 'serif-sm' | 'serif-xs'
    /** What type of button should be included, if any? */
    btnVariant?: 'close' | 'collapse' | 'expand'
    /** What should the aria label be for the button (if applicable)? */
    buttonAriaLabel?: string
  }>(),
  {
    location: 'modal',
    variant: 'default',
    headingSize: 'serif'
  }
)

defineEmits<{
  (e: 'btnClicked'): void
}>()

const locationMapping = {
  modal: 'p-padding-panel-container-horz sm:pt-padding-panel-container-vert',
  panel: 'px-padding-panel-container-horz pt-4',
  legendCollapsed:
    'px-padding-default py-stack-tight sm:px-padding-panel-container-horz sm:py-2'
}

const variantMapping = {
  default: {
    container: '',
    textColor: 'text-default'
  },
  warn: {
    container: 'bg-warning',
    textColor: 'text-emphasis'
  },
  destructive: {
    container: 'bg-error',
    textColor: 'text-inverse'
  }
}
</script>

<template>
  <header
    class="w-full sm:rounded-t flex items-center justify-between space-x-2"
    :class="[locationMapping[location], variantMapping[variant].container]"
  >
    <UIHeading
      tag="h4"
      :variant="headingSize"
      :text="text"
      :class="variantMapping[variant].textColor"
      class="text-center sm:text-left"
    ></UIHeading>
    <UICloseButton
      v-if="btnVariant === 'close'"
      tool-tip-direction="bottom"
      :aria-label="buttonAriaLabel"
      @close="$emit('btnClicked')"
    />
    <UICollapseButton
      v-else-if="btnVariant"
      :variant="btnVariant"
      :aria-label="buttonAriaLabel"
      @click="$emit('btnClicked')"
    />
  </header>
</template>
